import { useContext, useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import { useNavigate } from 'react-router';
import { useTab } from '@ayocore/platform/client/tabs';
import {
  usePartnerRepository,
  useContactPersonRepository,
  useLeadRepository,
  useDealRepository,
} from '../../../rpc';
import { useSupplierDocRepository } from '@ayocore/erp/accounting/client/rpc';
import { PartnerViewController, type PartnerViewState } from './PartnerViewController';
import { DrawerContext } from '@ayocore/platform/client/components/DrawerHost/DrawerContext';

/**
 * Добавляет экшены контроллера к снапшоту состояния для удобного потребления в компонентах.
 */
function withActions(
  controller: PartnerViewController,
  snapshot: PartnerViewState,
  partnerId: number,
  isModal: boolean,
  setTabTitle: (title: string) => void,
  navigate: ReturnType<typeof useNavigate>,
  partnerRepository: ReturnType<typeof usePartnerRepository>,
  contactPersonRepository: ReturnType<typeof useContactPersonRepository>,
  leadRepository: ReturnType<typeof useLeadRepository>,
  dealRepository: ReturnType<typeof useDealRepository>,
  closeDrawer?: () => void
) {
  return {
    // State
    partner: snapshot.partner,
    partnerStats: snapshot.partnerStats,
    partnerChart: snapshot.partnerChart,
    contactPersons: snapshot.contactPersons,
    leads: snapshot.leads,
    deals: snapshot.deals,
    contactChannels: snapshot.contactChannels,
    segments: snapshot.segments,
    tags: snapshot.tags,
    purchasesCount: snapshot.purchasesCount,
    loading: snapshot.loading,
    error: snapshot.error,
    permissions: snapshot.permissions,
    modals: snapshot.modals,

    // Actions
    openEdit: controller.openEdit.bind(controller),
    closeEdit: controller.closeEdit.bind(controller),
    openDelete: controller.openDelete.bind(controller),
    closeDelete: controller.closeDelete.bind(controller),
    deletePartner: () =>
      controller.deletePartner(partnerId, navigate, partnerRepository, isModal, closeDrawer),
    refreshContacts: () => controller.refreshContacts(partnerId, partnerRepository),
    refreshContactPersons: () =>
      controller.refreshContactPersons(partnerId, contactPersonRepository),
    refreshSegments: () => controller.refreshSegments(partnerId, partnerRepository),
    refreshTags: () => controller.refreshTags(partnerId, partnerRepository),
    assignSegment: (segmentId: number) =>
      controller.assignSegment(partnerId, segmentId, partnerRepository),
    removeSegment: (segmentId: number) =>
      controller.removeSegment(partnerId, segmentId, partnerRepository),
    assignTag: (tagId: number) => controller.assignTag(partnerId, tagId, partnerRepository),
    removeTag: (tagId: number) => controller.removeTag(partnerId, tagId, partnerRepository),
    updatePartnerNotes: (notes: string) => controller.updatePartnerNotes(notes),
    archivePartner: () => controller.archivePartner(partnerId, partnerRepository),
    unarchivePartner: () => controller.unarchivePartner(partnerId, partnerRepository),
    refreshPartner: () => controller.refreshPartner(partnerId, partnerRepository),
    /** Перепроверить права: нужно после изменений, влияющих на точечные guard'ы (например, ролей партнёра). */
    refreshPermissions: () => controller.checkPermissions(partnerId, partnerRepository),
    reload: () =>
      controller.reload(
        partnerId,
        isModal,
        setTabTitle,
        navigate,
        partnerRepository,
        contactPersonRepository,
        leadRepository,
        dealRepository
      ),
  };
}

/**
 * usePartnerViewController — реактивный хук для работы с PartnerView через useSyncExternalStore.
 *
 * Поведение:
 * - Подписывается на `PartnerViewController` и возвращает актуальный срез состояния.
 * - Поддерживает селектор: если передан, вернёт выбранный фрагмент; иначе вернёт полный объект
 *   состояния вместе с экшенами.
 * - Внутренне использует стабильный снапшот контроллера (без копирования), что корректно для
 *   concurrent rendering.
 * - Автоматически загружает данные и проверяет права при изменении partnerId.
 *
 * Примеры:
 *   const view = usePartnerViewController(partnerId);                    // полный доступ к state + actions
 *   const partner = usePartnerViewController(partnerId, s => s.partner);        // только partner
 */
export function usePartnerViewController(
  partnerId: number,
  isModal?: boolean
): ReturnType<typeof withActions>;
export function usePartnerViewController<S>(
  partnerId: number,
  isModal: boolean | undefined,
  selector: (full: ReturnType<typeof withActions>) => S
): S;
export function usePartnerViewController<S = ReturnType<typeof withActions>>(
  partnerId: number,
  isModal: boolean = false,
  selector?: (full: ReturnType<typeof withActions>) => S
): S | ReturnType<typeof withActions> {
  const controllerRef = useRef<PartnerViewController | null>(null);
  if (!controllerRef.current) {
    controllerRef.current = new PartnerViewController();
  }
  const controller = controllerRef.current;
  const { setTabTitle } = useTab();
  const navigate = useNavigate();
  const partnerRepository = usePartnerRepository();
  const contactPersonRepository = useContactPersonRepository();
  const leadRepository = useLeadRepository();
  const dealRepository = useDealRepository();
  const supplierDocRepository = useSupplierDocRepository();
  const drawerCtx = useContext(DrawerContext);
  const closeDrawer = drawerCtx?.close;

  // Стабилизируем репозитории через useRef, чтобы избежать бесконечных циклов
  const partnerRepoRef = useRef(partnerRepository);
  const contactPersonRepoRef = useRef(contactPersonRepository);
  const leadRepoRef = useRef(leadRepository);
  const dealRepoRef = useRef(dealRepository);
  const supplierDocRepoRef = useRef(supplierDocRepository);
  const setTabTitleRef = useRef(setTabTitle);
  const navigateRef = useRef(navigate);

  useEffect(() => {
    partnerRepoRef.current = partnerRepository;
    contactPersonRepoRef.current = contactPersonRepository;
    leadRepoRef.current = leadRepository;
    dealRepoRef.current = dealRepository;
    supplierDocRepoRef.current = supplierDocRepository;
    setTabTitleRef.current = setTabTitle;
    navigateRef.current = navigate;
  }, [partnerRepository, contactPersonRepository, leadRepository, dealRepository, supplierDocRepository, setTabTitle, navigate]);

  // Загружаем данные и проверяем права при изменении partnerId
  useEffect(() => {
    if (!partnerId || isNaN(partnerId)) {
      return;
    }

    controller.load(
      partnerId,
      isModal,
      setTabTitleRef.current,
      navigateRef.current,
      partnerRepoRef.current,
      contactPersonRepoRef.current,
      leadRepoRef.current,
      dealRepoRef.current
    );
    controller.checkPermissions(partnerId, partnerRepoRef.current);

    // Считаем закупки параллельно с основной загрузкой — результат определяет видимость вкладки
    supplierDocRepoRef.current
      .filterByPartner(partnerId)
      .count()
      .then(count => controller.setPurchasesCount(count))
      .catch(() => controller.setPurchasesCount(0));
  }, [partnerId, isModal, controller]);

  // Подписываемся на изменения состояния через useSyncExternalStore
  const state = useSyncExternalStore(
    controller.subscribe,
    controller.getSnapshot,
    controller.getSnapshot
  );

  // Создаём полный объект с экшенами
  const full = useMemo(
    () =>
      withActions(
        controller,
        state,
        partnerId,
        isModal,
        setTabTitle,
        navigate,
        partnerRepository,
        contactPersonRepository,
        leadRepository,
        dealRepository,
        closeDrawer
      ),
    [
      controller,
      state,
      partnerId,
      isModal,
      setTabTitle,
      navigate,
      partnerRepository,
      contactPersonRepository,
      leadRepository,
      dealRepository,
      closeDrawer,
    ]
  );

  // Если селектор не передан, возвращаем полный объект
  if (!selector) return full;

  // Если селектор передан, применяем его с мемоизацией
  const selectedRef = useRef<S | undefined>(undefined);
  return useMemo(() => {
    const selected = selector(full);
    if (selectedRef.current !== undefined) {
      try {
        if (JSON.stringify(selectedRef.current) === JSON.stringify(selected)) {
          return selectedRef.current;
        }
      } catch {
        /* noop */
      }
    }
    selectedRef.current = selected;
    return selected;
  }, [full, selector]);
}
